import React, { useEffect, useMemo, useState } from "react";
import { Box, Text } from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import { AppBar } from "../../../components/mobileUi";
import {
  DesktopBack,
  DesktopPage,
  DesktopPanel,
  useIsDesktop,
} from "../../../components/desktopUi";
import {
  ApproverCard,
  DetailStatusPill,
  DetailTabBar,
  FeedbackTab,
  ImageBlock,
} from "../../../components/financeDetail";
import { CenterSpinner, EmptyText, M, PropCell } from "../../../components/managementUi";
import { formatMoney } from "../../../utils/financeFormat";
import { SELL_TYPES } from "./sellConfig";
import { EMP_BASE_PATH } from "../../../config";

const fmtDate = (v) => (v ? moment(v).format("DD/MM/YYYY") : "-");
const apartmentText = (a) => (a?.code ? `${a.code}${a?.name ? ` (${a.name})` : ""}` : "-");

/**
 * Chi tiết giao dịch (Quản lý bán hàng).
 * Bố cục + danh sách field khớp *ManagerOverviewTab của app cho từng loại.
 */
const SellTransactionDetail = () => {
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();
  const { type, id } = useParams();
  const cfg = SELL_TYPES[type];

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0);

  useEffect(() => {
    if (!cfg || !id) return;
    setLoading(true);
    cfg.detailApi(id)
      .then((d) => setDetail(d || null))
      .catch(() => setDetail(null))
      .finally(() => setLoading(false));
  }, [cfg, id]);

  // Tab hiển thị theo dữ liệu có thật - khớp app.
  const tabs = useMemo(() => {
    const list = [{ key: "overview", title: "Tổng quan" }];
    if (!detail) return list;
    if (type === "ttkq" || detail?.images?.length) list.push({ key: "transaction", title: "Giao dịch" });
    if (type === "locking" && detail?.relatives?.length) list.push({ key: "participant", title: "Thành viên" });
    if (detail?._id) list.push({ key: "interactNote", title: "Phản hồi" });
    return list;
  }, [detail, type]);

  useEffect(() => {
    if (tab >= tabs.length) setTab(0);
  }, [tabs.length, tab]);

  if (!cfg) {
    return (
      <Box maxW="480px" mx="auto">
        <AppBar title="Chi tiết giao dịch" />
        <EmptyText text="Không tìm thấy loại giao dịch" />
      </Box>
    );
  }

  const activeKey = tabs[tab]?.key;
  const approver = detail?.bookingHistory?.approver || detail?.approver;
  const approvedAt = detail?.bookingHistory?.updatedAt || detail?.approvedAt;

  const headerTitle = detail?.project?.name
    ? `${detail.project.name} - ${cfg.apartmentCode(detail) || "Không định danh"}`
    : cfg.apartmentCode(detail) || "-";

  const goProject = detail?.projectId
    ? () => navigate(`${EMP_BASE_PATH}/tai-chinh/du-an/${detail.projectId}`)
    : undefined;
  const goCustomer = detail?.customerId
    ? () => navigate(`${EMP_BASE_PATH}/tai-chinh/khach-hang/${detail.customerId}`)
    : undefined;
  const goEvent = detail?.saleOpeningEventId
    ? () => navigate(`${EMP_BASE_PATH}/tai-chinh/dot-mo-ban/${detail.saleOpeningEventId}`)
    : undefined;

  const renderOverview = () => {
    if (type === "booking") {
      return (
        <>
          <PropCell label="Dự án" value={detail?.project?.name || "-"} onPress={goProject} />
          <PropCell label="Căn hộ" value={apartmentText(detail?.apartment)} />
          <PropCell label="Giá trị niêm yết" value={formatMoney(detail?.apartment?.totalValue || 0)} />
          <PropCell label="Khách hàng" value={detail?.customer?.name || "-"} onPress={goCustomer} />
          <PropCell label="Nhân viên tạo" value={detail?.booker?.fullName || "-"} />
          <PropCell label="Ngày vào cọc" value={fmtDate(detail?.bookingTime)} />
          <PropCell label="Ngày tạo booking" value={fmtDate(detail?.createdAt)} />
          <PropCell label="Số tiền đặt cọc" value={formatMoney(detail?.price || 0)} />
          <PropCell label="Ghi chú" value={detail?.note || "-"} borderBottom={false} />
        </>
      );
    }

    if (type === "locking") {
      return (
        <>
          <PropCell label="Dự án" value={detail?.project?.name || "-"} onPress={goProject} />
          <PropCell label="Đợt mở bán" value={detail?.saleOpeningEvent?.name || "-"} onPress={goEvent} />
          {detail?.booking?.code ? <PropCell label="Mã booking" value={detail.booking.code} /> : null}
          <PropCell label="Căn hộ" value={apartmentText(detail?.apartment)} />
          <PropCell label="Giá trị niêm yết" value={formatMoney(detail?.apartment?.totalValue || 0)} />
          <PropCell label="Khách hàng" value={detail?.customer?.name || "-"} onPress={goCustomer} />
          <PropCell label="Nhân viên tạo" value={detail?.booker?.fullName || "-"} />
          {detail?.bookingId ? (
            <PropCell
              label="Trạng thái booking"
              value="Xem chi tiết booking"
              onPress={() => navigate(`${EMP_BASE_PATH}/quan-ly/ban-hang/booking/${detail.bookingId}`)}
            />
          ) : (
            <PropCell label="Trạng thái booking" value="-" />
          )}
          <PropCell label="Ngày vào cọc" value={fmtDate(detail?.bookingTime)} />
          <PropCell label="Ngày tạo locking" value={fmtDate(detail?.createdAt)} />
          <PropCell label="Số tiền đặt cọc" value={formatMoney(detail?.price || 0)} />
          <PropCell label="Ghi chú" value={detail?.note || "-"} borderBottom={false} />
        </>
      );
    }

    if (type === "hdmb") {
      const apt = detail?.locking?.apartment || detail?.ttkq?.locking?.apartment;
      return (
        <>
          <PropCell label="Dự án" value={detail?.project?.name || "-"} onPress={goProject} />
          {detail?.code ? <PropCell label="Mã HDMB" value={detail.code} /> : null}
          <PropCell label="Căn hộ" value={apartmentText(apt)} />
          <PropCell label="Khách hàng" value={detail?.customer?.name || "-"} onPress={goCustomer} />
          <PropCell label="Người lập HDMB" value={detail?.creator?.fullName || "-"} />
          <PropCell label="Phường thức thánh toán" value={detail?.paymentMethod?.name || "-"} />
          {detail?.lockingId ? (
            <PropCell
              label="Trạng thái locking"
              value="Xem chi tiết locking"
              onPress={() => navigate(`${EMP_BASE_PATH}/quan-ly/ban-hang/locking/${detail.lockingId}`)}
            />
          ) : null}
          {detail?.ttkqId ? (
            <PropCell
              label="Trạng thái ttkq"
              value="Xem chi tiết ttkq"
              onPress={() => navigate(`${EMP_BASE_PATH}/quan-ly/ban-hang/ttkq/${detail.ttkqId}`)}
            />
          ) : null}
          <PropCell label="Ngày ký HDMB" value={fmtDate(detail?.time)} />
          <PropCell label="Ngày đóng tiền HDMB" value={fmtDate(detail?.priceTime)} />
          <PropCell label="Ngày tạo HDMB" value={fmtDate(detail?.createdAt)} />
          <PropCell label="Số tiền đóng HDMB" value={formatMoney(detail?.price || 0)} />
          <PropCell label="Ghi chú" value={detail?.note || "-"} borderBottom={false} />
        </>
      );
    }

    // ttkq
    return (
      <>
        <PropCell label="Dự án" value={detail?.project?.name || "-"} onPress={goProject} />
        {detail?.code ? <PropCell label="Mã TTKQ" value={detail.code} /> : null}
        <PropCell label="Căn hộ" value={apartmentText(detail?.locking?.apartment)} />
        <PropCell label="Khách hàng" value={detail?.customer?.name || "-"} onPress={goCustomer} />
        <PropCell
          label="Người lập TTKQ"
          value={`${detail?.creator?.fullName || "-"} (${detail?.creator?.code || "-"})`}
        />
        <PropCell label="Phường thức thánh toán" value={detail?.paymentMethod?.name || "-"} />
        <PropCell label="Ngày ký TTKQ" value={fmtDate(detail?.time)} />
        <PropCell label="Ngày đóng tiền TTKQ" value={fmtDate(detail?.priceTime)} />
        <PropCell label="Ngày tạo TTKQ" value={fmtDate(detail?.createdAt)} />
        <PropCell label="Số tiền đóng TTKQ" value={formatMoney(detail?.price || 0)} />
        <PropCell label="Ghi chú" value={detail?.note || "-"} borderBottom={false} />
      </>
    );
  };

  const renderTab = () => {
    if (activeKey === "overview") {
      return (
        <Box pt="16px">
          <Text fontSize="16px" fontWeight={600} color={M.baseContent}>
            {headerTitle}
          </Text>
          <Box h="8px" />
          <DetailStatusPill status={detail?.status} />
          <ApproverCard approver={approver} dateLabel={fmtDate(approvedAt)} />
          {renderOverview()}
        </Box>
      );
    }

    if (activeKey === "transaction") {
      return (
        <Box pt="16px">
          <ImageBlock images={detail?.images} emptyText="Chưa có ảnh giao dịch" />
        </Box>
      );
    }

    if (activeKey === "participant") {
      const list = detail?.relatives || [];
      if (!list.length) return <EmptyText text="Chưa có thành viên" />;
      return (
        <Box pt="16px">
          {list.map((r, i) => (
            <PropCell
              key={r?._id || i}
              label={r?.jobPosition?.name || "Thành viên"}
              value={`${r?.fullName || "-"}${r?.code ? ` (${r.code})` : ""}`}
            />
          ))}
        </Box>
      );
    }

    return (
      <Box pt="16px">
        <FeedbackTab query={{ [cfg.historyKey]: id }} />
      </Box>
    );
  };

  const pageBody = (
    <>
      {loading ? (
        <CenterSpinner />
      ) : !detail ? (
        <EmptyText text="Không tìm thấy giao dịch" />
      ) : (
        <>
          <DetailTabBar tabs={tabs} index={tab} onChange={setTab} />
          <Box px="16px">{renderTab()}</Box>
        </>
      )}
    </>
  );

  // ===================== Bản WEB (desktop) =====================
  if (isDesktop) {
    return (
      <DesktopPage title={cfg.detailTitle} right={<DesktopBack onClick={() => navigate(-1)} />}>
        <DesktopPanel p="0" pb="24px">
          {pageBody}
        </DesktopPanel>
      </DesktopPage>
    );
  }

  return (
    <Box maxW="480px" mx="auto" pb="40px">
      <AppBar title={cfg.detailTitle} />
      {pageBody}
    </Box>
  );
};

export default SellTransactionDetail;
